import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastContext, ToastContextProps } from 'contexts/ToastContext';
import {
    DepuisQuandQuestionnaireMainWrapper,
    DepuisQuandSliderWrapper,
    // DepuisQuandProgressWrapper,
} from 'pages/questionnaire/slides/depuisquand/DepuisQuandQuestionnaireMain.styled';
// import ProgressBar from 'components/common/progress-bar/ProgressBar';
import { SwiperSlide, useSwiper } from 'swiper/react';
import Slider from 'components/slider/Slider';
import DepuisQuandQuestionSlide, {
    DepuisQuandQuestionSlideProps,
} from 'pages/questionnaire/slides/depuisquand/DepuisQuandQuestionSlide';
import { Swiper as SwiperClass } from 'swiper/types';
import { Questionnaire } from 'types/app';
import useAPIQuestionnaire from 'hooks/api/useAPIQuestionnaire';

export interface DepuisQuandQuestionnaireMainProps {
    onComplete?: () => void;
    questionnaireData: Questionnaire.IIpsativeQuestionnaire;
    onProgressUpdate?: (id: string, current: number, total: number) => void;
}

const DepuisQuandQuestionnaireMain: React.FC<DepuisQuandQuestionnaireMainProps> = ({
    onComplete,
    questionnaireData,
    // onProgressUpdate,
}) => {
    const { t } = useTranslation('translation');
    const parentSwiper = useSwiper();
    const { sendDepuisQuandResponse, startQuestionTracking, getCurrentTiming } = useAPIQuestionnaire();
    // const [progression, updateProgression] = useState(questionnaireData.progression);
    const questionnaire = useRef<Questionnaire.IIpsativeQuestionnaire>(questionnaireData);
    const swiperInstance = useRef<SwiperClass>();
    const [slidesContents, setSlidesContents] = useState<Array<DepuisQuandQuestionSlideProps>>([]);

    const { setToast } = useContext(ToastContext) as ToastContextProps;
    // Guards against double taps. A second tap while a response is being sent,
    // while the questionnaire is completing, or while the slider is still
    // animating would call gotoNext twice and silently skip a question.
    const isSending = useRef(false);
    const isCompleting = useRef(false);
    const isSliderAnimating = () => !!swiperInstance.current?.animating;
    const isNavigationLocked = () =>
        isSending.current || isCompleting.current || isSliderAnimating();

    const updateSlidesContents = () => {
        const slideIndex = swiperInstance.current?.realIndex;
        const theQuestionnaire = questionnaire.current;

        const nextQuestionIndex = theQuestionnaire.currentQuestionIndex + 1;
        const prevQuestionIndex = theQuestionnaire.currentQuestionIndex - 1;
        const currentQuestion = theQuestionnaire.questions[theQuestionnaire.currentQuestionIndex];
        const nextQuestion = theQuestionnaire.questions[nextQuestionIndex];
        const previousQuestion = theQuestionnaire.questions[prevQuestionIndex];

        const slideCurrent: DepuisQuandQuestionSlideProps = {
            question: currentQuestion,
            gotoNext:
                nextQuestion?.selectedAnswerId !== undefined ||
                    currentQuestion?.selectedAnswerId !== undefined
                    ? gotoNext
                    : undefined,
            gotoPrevious: previousQuestion ? gotoPrevious : undefined,
            onAnswer,
            questionIndex: theQuestionnaire.currentQuestionIndex,
            totalQuestions: theQuestionnaire.questions.length,
        };

        const slideNext: DepuisQuandQuestionSlideProps = {
            question: nextQuestion,
            gotoNext:
                theQuestionnaire.questions[nextQuestionIndex + 1]?.selectedAnswerId !== undefined ||
                    nextQuestion?.selectedAnswerId !== undefined
                    ? gotoNext
                    : undefined,
            gotoPrevious,
            onAnswer,
            questionIndex: nextQuestionIndex,
            totalQuestions: theQuestionnaire.questions.length,
        };

        const slidePrevious: DepuisQuandQuestionSlideProps = {
            question: previousQuestion,
            gotoNext,
            gotoPrevious:
                theQuestionnaire.questions[prevQuestionIndex - 1] ? gotoPrevious : undefined,
            onAnswer,
            questionIndex: prevQuestionIndex,
            totalQuestions: theQuestionnaire.questions.length,
        };

        if (slideIndex !== undefined && swiperInstance.current) {
            const totalSlides = swiperInstance.current.slides.length;
            const currentSlideIndex = slideIndex;
            const nextSlideIndex = slideIndex + 1 >= totalSlides ? 0 : slideIndex + 1;
            const prevSlideIndex = slideIndex - 1 < 0 ? totalSlides - 1 : slideIndex - 1;
            const contents: Array<DepuisQuandQuestionSlideProps> = [];

            contents[currentSlideIndex] = slideCurrent;
            contents[nextSlideIndex] = slideNext;
            contents[prevSlideIndex] = slidePrevious;

            setSlidesContents(contents);
        }
    };

    const onInitSwiper = (swiper: SwiperClass) => {
        swiperInstance.current = swiper;
        updateSlidesContents();
    };

    const onSwiperSlideChange = () => {
        // Create new tracker for current question (type_question: 3 for depuis_quand)
        const currentQuestion = questionnaire.current.questions[questionnaire.current.currentQuestionIndex];
        if (currentQuestion && currentQuestion.id) {
            startQuestionTracking();
        }
        
        updateSlidesContents();
    };

    const gotoPrevious = () => {
        if (isNavigationLocked()) return;
        const prevIndex = questionnaire.current.currentQuestionIndex - 1;
        questionnaire.current.currentQuestionIndex = prevIndex > 0 ? prevIndex : 0;
        swiperInstance.current?.slidePrev();
    };

    const gotoNext = () => {
        if (isNavigationLocked()) return;
        const nextIndex = questionnaire.current.currentQuestionIndex + 1;
        if (!questionnaire.current.questions[nextIndex]) {
            // Last question reached: go back to any unanswered question instead
            // of looping on the last one forever.
            gotoFirstUnanswered();
            return;
        }
        questionnaire.current.currentQuestionIndex = nextIndex;
        swiperInstance.current?.slideNext();
    };

    const gotoFirstUnanswered = () => {
        const firstUnansweredIndex = questionnaire.current.questions.findIndex(
            (question) => question.selectedAnswerId === undefined,
        );
        if (firstUnansweredIndex < 0) {
            // Everything is answered: move on to the next part.
            complete();
            return;
        }
        setToast({ message: t('questionnaire.unanswered-question'), status: 'warning' });
        questionnaire.current.currentQuestionIndex = firstUnansweredIndex;
        swiperInstance.current?.slideNext();
    };

    const complete = () => {
        if (onComplete) {
            onComplete();
        } else {
            parentSwiper.slideNext();
        }
    };

    const updateQuestionnaire = (qAnswered: Questionnaire.IIpsativeQuestion) => {
        const tmpQnnaire: Questionnaire.IIpsativeQuestionnaire = { ...questionnaire.current! };
        const questions = [...tmpQnnaire.questions];

        questions.forEach((q) => {
            if (q.id === qAnswered.id) {
                q.selectedAnswerId = qAnswered.selectedAnswerId;
            }
        });

        const nbQAnswered = questions.filter((item) => item.selectedAnswerId !== undefined).length || 0;
        tmpQnnaire.progression = Math.floor((nbQAnswered / tmpQnnaire.total) * 100);

        questionnaire.current = tmpQnnaire;
        // updateProgression(tmpQnnaire.progression);
    };

    const onAnswer = async (qAnswered: Questionnaire.IIpsativeQuestion) => {
        if (qAnswered.selectedAnswerId === undefined) return;
        if (isNavigationLocked()) return;

        const timing = getCurrentTiming();
        let success = false;
        isSending.current = true;
        try {
            success = await sendDepuisQuandResponse(
                questionnaire.current.id,
                qAnswered.id,
                qAnswered.selectedAnswerId,
                qAnswered.order,
                timing || undefined,
            );
        } finally {
            isSending.current = false;
        }

        if (!success) return;
        updateQuestionnaire(qAnswered);

        // Check if questionnaire is complete
        if (questionnaire.current.progression === 100) {
            isCompleting.current = true;
            // Small delay before transitioning
            setTimeout(complete, 300);
        } else {
            gotoNext();
        }
    };

    useEffect(() => {
        updateSlidesContents();
        // Start tracking for the first question when component becomes active
        const currentQuestion = questionnaire.current.questions[questionnaire.current.currentQuestionIndex];
        if (currentQuestion && currentQuestion.id) {
            startQuestionTracking();
        }
    }, []);

    return (
        <DepuisQuandQuestionnaireMainWrapper>
            <DepuisQuandSliderWrapper>
                <Slider
                    initialSlide={1}
                    onInit={onInitSwiper}
                    allowTouchMove={false}
                    onSlideChangeTransitionEnd={onSwiperSlideChange}
                    loop
                >
                    <SwiperSlide>
                        <DepuisQuandQuestionSlide
                            {...slidesContents[0]}
                            key={slidesContents[0]?.question ? `${slidesContents[0].question.id}` : 0}
                        />
                    </SwiperSlide>
                    <SwiperSlide>
                        <DepuisQuandQuestionSlide
                            {...slidesContents[1]}
                            key={slidesContents[1]?.question ? `${slidesContents[1].question.id}` : 1}
                        />
                    </SwiperSlide>
                    <SwiperSlide>
                        <DepuisQuandQuestionSlide
                            {...slidesContents[2]}
                            key={slidesContents[2]?.question ? `${slidesContents[2].question.id}` : 2}
                        />
                    </SwiperSlide>
                </Slider>
            </DepuisQuandSliderWrapper>
            {/* {
                progression < 100 && (
                    <DepuisQuandProgressWrapper>
                        <ProgressBar
                            percent={progression}
                            title={`${t('questionnaire.depuisquand.progression-title')}`}
                        />
                    </DepuisQuandProgressWrapper>
                )
            } */}
        </DepuisQuandQuestionnaireMainWrapper >
    );
};

export default DepuisQuandQuestionnaireMain;
